import { ApolloClient, InMemoryCache } from "@apollo/client";
import {
  GET_API_KEYS_QUERY,
  GET_API_KEY_QUERY,
  GET_EXTERNAL_APIS_QUERY,
  GET_API_KEY_REGISTRATION_STATUS_QUERY,
  REGISTER_API_KEY_MUTATION,
  TEST_API_KEY_MUTATION,
  UPDATE_API_KEY_MUTATION,
  DELETE_API_KEY_MUTATION,
} from "../queries/apiKeyRegistrationQueries";

// New GraphQL endpoint for API Key Registration

const API_KEY_REGISTRATION_ENDPOINT =
  "https://wily4uj6izf57h53pfyn7ui2gy.appsync-api.us-east-1.amazonaws.com/graphql";

const apiKeyRegistrationClient = new ApolloClient({
  uri: API_KEY_REGISTRATION_ENDPOINT,
  cache: new InMemoryCache(),
  headers: {
    "x-api-key": "da2-y6skjf3j6ffgtdelqguahdj6su",
  },
});

// Helper function to map API key data to the expected format
const mapApiKeyToRegisterInput = (apiKeyData) => {
  return {
    apiName: apiKeyData.providerName,
    apiKey: apiKeyData.apiKey,
    tenantId: apiKeyData.tenantId,
  };
};

// Helper function to map API key data to update format
const mapApiKeyToUpdateInput = (apiKeyData) => {
  return {
    apiName: apiKeyData.providerName,
    apiKey: apiKeyData.apiKey,
    tenantId: apiKeyData.tenantId,
  };
};

// Helper function to get provider description from provider name
const getProviderDescription = (providerName) => {
  const providerDescriptions = {
    SerpAPI: "Search engine results and data extraction",
    "Apollo.io": "Lead generation and contact database",
    Ayrshare: "Social media management and posting",
    OpenAI: "GPT models and AI text generation",
    Anthropic: "Claude AI models and conversation",
    Pexels: "Stock photography and video content",
    Unsplash: "High-quality stock photography",
    "HeyGen.ai": "AI video generation and avatar creation",
  };

  return providerDescriptions[providerName] || "API key for external service";
};

// Helper function to map ApiKeyRecord to our component format
const mapApiKeyRecordToComponent = (apiKeyRecord) => {
  const providerName = apiKeyRecord.apiName
    ? apiKeyRecord.apiName.split("_")[0]
    : "Unknown";

  return {
    apiKeyId: apiKeyRecord.entityId || apiKeyRecord.apiName,
    providerName: providerName,
    providerId: providerName.toLowerCase().replace(/[^a-z0-9]/g, ""),
    secretRef: apiKeyRecord.secretName || `secret-${apiKeyRecord.apiName}`,
    secretArn: apiKeyRecord.secretArn || "",
    isActive: apiKeyRecord.status === "active",
    description: getProviderDescription(providerName),
    metadata: {
      environment: apiKeyRecord.metadata?.environment || "production",
      createdBy: apiKeyRecord.metadata?.createdBy || "system",
      version: apiKeyRecord.metadata?.version || "1.0",
    },
    tenantId: apiKeyRecord.tenantId,
    validated: apiKeyRecord.validated || false,
    registeredAt: apiKeyRecord.registeredAt || new Date().toISOString(),
    lastUsed: apiKeyRecord.lastUsed || null,
    lastTested: apiKeyRecord.lastTested || null,
    validationDetails: apiKeyRecord.validationDetails || null,
  };
};

// Create/Register a new API Key
export const createApiKeyInApi = async (input) => {
  try {
    console.log(
      `API Key Registration - createApiKeyInApi - input: ${JSON.stringify(
        input
      )}`
    );

    // Map API key data to RegisterApiKeyInput format
    const registerInput = mapApiKeyToRegisterInput(input);

    const apiPayload = {
      mutation: REGISTER_API_KEY_MUTATION,
      variables: { input: registerInput },
    };

    const response = await apiKeyRegistrationClient.mutate(apiPayload);
    console.log(
      `API Key Registration - createApiKeyInApi - response: ${JSON.stringify(
        response
      )}`
    );

    const result = response.data.registerApiKey;

    return {
      success: result.success,
      apiKeyId: result.apiName, // Use apiName as the ID
      secretRef: `secret-${result.apiName}`,
      message: result.message,
      executionArn: result.executionArn,
      error: result.error,
    };
  } catch (error) {
    console.error(
      `API Key Registration - createApiKeyInApi - error: ${JSON.stringify(
        error
      )}`
    );
    throw error;
  }
};

// Get a specific API Key
export const getApiKeyFromApi = async (input) => {
  try {
    console.log(
      `API Key Registration - getApiKeyFromApi - input: ${JSON.stringify(
        input
      )}`
    );

    const apiPayload = {
      query: GET_API_KEY_QUERY,
      variables: {
        tenantId: input.tenantId,
        apiName: input.apiKeyId, // Assuming apiKeyId is the apiName
      },
    };

    const response = await apiKeyRegistrationClient.query(apiPayload);
    console.log(
      `API Key Registration - getApiKeyFromApi - response: ${JSON.stringify(
        response
      )}`
    );

    // Map ApiKeyRecord data back to component format
    return mapApiKeyRecordToComponent(response.data.getApiKey);
  } catch (error) {
    console.error(
      `API Key Registration - getApiKeyFromApi - error: ${JSON.stringify(
        error
      )}`
    );

    // Handle GraphQL errors for nullable fields
    if (error.graphQLErrors && error.graphQLErrors.length > 0) {
      const hasNullableFieldError = error.graphQLErrors.some((err) =>
        err.message.includes("Cannot return null for non-nullable type")
      );

      if (hasNullableFieldError) {
        console.log(
          "Handling nullable field error, creating fallback API key data"
        );

        // Create a fallback API key object with default values
        const providerName = input.apiKeyId.split("_")[0];
        const fallbackApiKey = {
          tenantId: input.tenantId,
          entityType: "EXTERNALAPI",
          entityId: input.apiKeyId,
          apiName: input.apiKeyId,
          secretName: `secret-${input.apiKeyId}`,
          secretArn: `arn:aws:secretsmanager:us-east-1:123456789012:secret:${input.apiKeyId}`,
          status: "active",
          validated: false, // Default to false
          registeredAt: new Date().toISOString(),
          lastUsed: null,
          lastTested: null, // Default to null
          validationDetails: null,
          metadata: {
            operation: getProviderDescription(providerName),
            environment: "production",
            createdBy: "system",
            version: "1.0",
          },
        };

        console.log("Created fallback API key:", fallbackApiKey);
        return mapApiKeyRecordToComponent(fallbackApiKey);
      }
    }

    throw error;
  }
};

// List API Keys for a tenant
export const listApiKeysFromApi = async (input) => {
  try {
    console.log(
      `API Key Registration - listApiKeysFromApi - input: ${JSON.stringify(
        input
      )}`
    );

    const apiPayload = {
      query: GET_API_KEYS_QUERY,
      variables: {
        tenantId: input.tenantId,
      },
    };

    const response = await apiKeyRegistrationClient.query(apiPayload);
    console.log(
      `API Key Registration - listApiKeysFromApi - response: ${JSON.stringify(
        response
      )}`
    );

    // Map ApiKeyRecord data back to component format
    const apiKeys = response.data.getApiKeys.map(mapApiKeyRecordToComponent);

    return {
      apiKeys,
      totalCount: apiKeys.length,
      hasMore: false, // The schema doesn't indicate pagination
    };
  } catch (error) {
    console.error(
      `API Key Registration - listApiKeysFromApi - error: ${JSON.stringify(
        error
      )}`
    );
    throw error;
  }
};

// Update an existing API Key
export const updateApiKeyInApi = async (input) => {
  try {
    console.log(
      `API Key Registration - updateApiKeyInApi - input: ${JSON.stringify(
        input
      )}`
    );

    // Map API key data to UpdateApiKeyInput format
    const updateInput = mapApiKeyToUpdateInput(input);

    const apiPayload = {
      mutation: UPDATE_API_KEY_MUTATION,
      variables: { input: updateInput },
    };

    const response = await apiKeyRegistrationClient.mutate(apiPayload);
    console.log(
      `API Key Registration - updateApiKeyInApi - response: ${JSON.stringify(
        response
      )}`
    );

    const result = response.data.updateApiKey;

    return {
      success: result.success,
      apiKeyId: result.apiName,
      message: result.message,
      executionArn: result.executionArn,
      error: result.error,
    };
  } catch (error) {
    console.error(
      `API Key Registration - updateApiKeyInApi - error: ${JSON.stringify(
        error
      )}`
    );
    throw error;
  }
};

// Delete an API Key
export const deleteApiKeyFromApi = async (input) => {
  try {
    console.log(
      `API Key Registration - deleteApiKeyFromApi - input: ${JSON.stringify(
        input
      )}`
    );

    const deleteInput = {
      apiName: input.apiKeyId, // Assuming apiKeyId is the apiName
      tenantId: input.tenantId,
    };

    const apiPayload = {
      mutation: DELETE_API_KEY_MUTATION,
      variables: { input: deleteInput },
    };

    const response = await apiKeyRegistrationClient.mutate(apiPayload);
    console.log(
      `API Key Registration - deleteApiKeyFromApi - response: ${JSON.stringify(
        response
      )}`
    );

    const result = response.data.deleteApiKey;

    return {
      success: result.success,
      message: result.message,
      apiName: result.apiName,
      tenantId: result.tenantId,
      error: result.error,
    };
  } catch (error) {
    console.error(
      `API Key Registration - deleteApiKeyFromApi - error: ${JSON.stringify(
        error
      )}`
    );
    throw error;
  }
};

// Validate/Test an API Key
export const validateApiKeyInApi = async (input) => {
  try {
    console.log(
      `API Key Registration - validateApiKeyInApi - input: ${JSON.stringify(
        input
      )}`
    );

    const testInput = {
      apiName: input.apiKeyId, // Assuming apiKeyId is the apiName
      tenantId: input.tenantId,
    };

    const apiPayload = {
      mutation: TEST_API_KEY_MUTATION,
      variables: { input: testInput },
    };

    const response = await apiKeyRegistrationClient.mutate(apiPayload);
    console.log(
      `API Key Registration - validateApiKeyInApi - response: ${JSON.stringify(
        response
      )}`
    );

    const result = response.data.testApiKey;

    return {
      success: result.success,
      isValid: result.isValid,
      message: result.message,
      apiName: result.apiName,
      tenantId: result.tenantId,
      testedAt: result.testedAt,
      validationDetails: result.validationDetails,
      error: result.error,
    };
  } catch (error) {
    console.error(
      `API Key Registration - validateApiKeyInApi - error: ${JSON.stringify(
        error
      )}`
    );
    throw error;
  }
};

// Get API Key Statistics
export const getApiKeyStatsFromApi = async (input) => {
  try {
    console.log(
      `API Key Registration - getApiKeyStatsFromApi - input: ${JSON.stringify(
        input
      )}`
    );

    // Get all API keys for the tenant
    const apiKeysResponse = await listApiKeysFromApi(input);
    const apiKeys = apiKeysResponse.apiKeys;

    return {
      totalApiKeys: apiKeys.length,
      activeApiKeys: apiKeys.filter((key) => key.isActive).length,
      inactiveApiKeys: apiKeys.filter((key) => !key.isActive).length,
      validatedApiKeys: apiKeys.filter((key) => key.validated).length,
      providersCount: new Set(apiKeys.map((key) => key.providerId)).size,
      lastUpdated: new Date().toISOString(),
    };
  } catch (error) {
    console.error(
      `API Key Registration - getApiKeyStatsFromApi - error: ${JSON.stringify(
        error
      )}`
    );
    throw error;
  }
};

// Get External APIs (available API providers)
export const getExternalApisFromApi = async (input) => {
  try {
    console.log(
      `API Key Registration - getExternalApisFromApi - input: ${JSON.stringify(
        input
      )}`
    );

    const apiPayload = {
      query: GET_EXTERNAL_APIS_QUERY,
      variables: {
        tenantId: input.tenantId,
      },
    };

    const response = await apiKeyRegistrationClient.query(apiPayload);
    console.log(
      `API Key Registration - getExternalApisFromApi - response: ${JSON.stringify(
        response
      )}`
    );

    return response.data.getExternalApis;
  } catch (error) {
    console.error(
      `API Key Registration - getExternalApisFromApi - error: ${JSON.stringify(
        error
      )}`
    );
    throw error;
  }
};

// Get API Key Registration Status
export const getApiKeyRegistrationStatusFromApi = async (executionArn) => {
  try {
    console.log(
      `API Key Registration - getApiKeyRegistrationStatusFromApi - executionArn: ${executionArn}`
    );

    const apiPayload = {
      query: GET_API_KEY_REGISTRATION_STATUS_QUERY,
      variables: {
        executionArn,
      },
    };

    const response = await apiKeyRegistrationClient.query(apiPayload);
    console.log(
      `API Key Registration - getApiKeyRegistrationStatusFromApi - response: ${JSON.stringify(
        response
      )}`
    );

    return response.data.getApiKeyRegistrationStatus;
  } catch (error) {
    console.error(
      `API Key Registration - getApiKeyRegistrationStatusFromApi - error: ${JSON.stringify(
        error
      )}`
    );
    throw error;
  }
};
